(ns basic-components.inputs.textarea
  (:require
   [basic-components.inputs.footprint :refer [editor viewer display-string]]
   [frontend.utils :as fu]
   [common.localization :as loc]
   [reagent.core :as r]
   [cljs-uuid-utils.core :as uuid]
   [reagent.dom :as rd]))

(defn render
  [dom-atom args scroll-calculator]
  (let [{:keys [value placeholder class-name
                has-focus? cursor-at-end?
                on-focus! on-blur! on-change!
                on-key-down! disabled? auto-focus]}
        args
        edit-value (or (:temp-value @dom-atom) value)
        render-value (or edit-value value)
        clear-state! (fn []
                       (swap! dom-atom dissoc :temp-value))
        show-toggle? (:show-toggle? @dom-atom)]
    [:div.textarea-field
     [:p
      {:class-name (fu/cs :textarea-field__autogrow
                          (when (:show-all? @dom-atom) :textarea-field__autogrow--show-all))}
      (str render-value " ")] ;; add extra space to ensure new line is rendered if as last char
     [:textarea
      (merge {:ref         (fn [e]
                             (when (and e
                                        (not= e (:input-dom-node @dom-atom)))
                               (swap! dom-atom assoc :input-dom-node e)
                               (.addEventListener e "keyup" scroll-calculator)))
              :placeholder (or placeholder "")
              :class-name  (fu/cs class-name
                                  "textarea-field__textarea"
                                  (when has-focus? "focus"))
              :value       render-value
              :on-change   (fn [e]
                             (let [new-value (-> e
                                                 .-target
                                                 .-value)]
                               (swap! dom-atom assoc :temp-value new-value)
                               (when on-change!
                                 (on-change! e new-value))))
              :on-focus    (fn [e]
                             (swap! dom-atom assoc :focus? true)
                             (fu/timeout 50                 ;; must do this in timeout since focus may be called before mount
                                         (fn []
                                           (if cursor-at-end?
                                             (fu/put-cursor-at-end! dom-atom)
                                             (.select (:input-dom-node @dom-atom)))))
                             (when on-focus!
                               (on-focus! e)))
              :on-blur     (fn [e]
                             (swap! dom-atom dissoc :focus?)
                             (when on-blur!
                               (on-blur! e render-value clear-state!)))
              :on-key-down on-key-down!
              :auto-focus  (boolean auto-focus)}
             (when disabled?
               {:disabled true}))]
     (when show-toggle?
       [:span.textarea-field__show-all
        {:on-click   (fn [e] (swap! dom-atom assoc :show-all? (not (:show-all? @dom-atom))))
         :class-name (fu/cs (if (:show-all? @dom-atom)
                              :textarea-field__show-less
                              :textarea-field__show-all))}
        (loc/tr (if (:show-all? @dom-atom)
              :show-less
              :show-all))])]))

(defmethod editor :editor.type/textarea
  [args]
  (let [dom-atom (or (:state-atom args) (r/atom nil))
        component-atom (r/atom nil)
        component-id (uuid/make-random-uuid)
        window-resize-listener-key (keyword "window-textarea-resize-listener" (uuid/uuid-string component-id))
        set-focusable! (:set-focusable-element! args)
        set-cancel-edit! (:set-cancel-edit! args)
        set-save! (:set-save! args)
        scroll-calculator (fn [_]
                            (let [dom-node (:input-dom-node @dom-atom)
                                  show-all? (:show-all? @dom-atom)
                                  scroll-height (or (some->> dom-node
                                                             .-scrollHeight)
                                                    0)
                                  offset-height (or (some->> dom-node
                                                             .-offsetHeight)
                                                    0)
                                  max-closed-height (or (:max-closed-height @component-atom) offset-height)
                                  has-scroll? (< offset-height scroll-height)] ;; only show when 3px hidden as chrome gives wrong values
                              (when (and (not show-all?)
                                         has-scroll?)
                                (swap! component-atom assoc :max-closed-height offset-height))
                              (if (and (not has-scroll?)                         ;does not have scroll
                                       (or (not show-all?)                       ;; already minimized
                                           (< offset-height max-closed-height))) ;; expanded value fits to closed view port
                                (swap! dom-atom assoc
                                       :show-toggle? false
                                       :show-all? false)
                                (swap! dom-atom assoc :show-toggle? true))))
        resize-tracker (fn [_]
                         (let [existing-handle (:resize-handle @component-atom)]
                           (when existing-handle
                             (js/clearTimeout existing-handle))
                           (swap! component-atom assoc
                                  :resize-handle
                                  (js/setTimeout (fn [_]
                                                   (js/clearTimeout (:resize-handle @component-atom))
                                                   (scroll-calculator nil))
                                                 5000))))   ;; you tend to spend some time resizing windows even if you do it seldom -> it's ok to recalculate 5s later
        cursor-at-end? (:cursor-at-end? args)]
    ;; ref vs atom
    (r/create-class
     {:display-name           "Textarea"
      :component-did-mount    (fn [mount-args]
                                (let []
                                  (when set-focusable!
                                    (set-focusable! (fn [_]
                                                      (when-let [dom-node (:input-dom-node @dom-atom)]
                                                        (.focus dom-node)))))

                                  (when (:auto-focus args)
                                    (js/setTimeout (fn [_]
                                                     (when-let [dom-node (:input-dom-node @dom-atom)]
                                                       (.focus dom-node)))
                                                   100))

                                  (when set-cancel-edit!
                                    (set-cancel-edit! #(swap! dom-atom dissoc :temp-value))) ;; save complete -> reset temp state
                                  (when set-save!
                                    (set-save! (fn [_]
                                                 (when-let [dom-node (:input-dom-node @dom-atom)]
                                                   (.blur dom-node)))))

                                  (swap! dom-atom assoc :window-resize-listener (.addEventListener js/window "resize" resize-tracker))
                                  (js/setTimeout scroll-calculator 100)))
      :component-did-update   (fn []
                                (js/setTimeout scroll-calculator 1000))
      :component-will-unmount (fn []
                                (.removeEventListener js/window "resize" (:window-resize-listener @dom-atom))
                                (swap! dom-atom dissoc :input-dom-node))
      :reagent-render         (fn [args]
                                (render dom-atom args scroll-calculator))})))

(defmethod display-string :editor.type/textarea
  [args]
  (str (:value args)))

(defmethod viewer :editor.type/textarea
  [args]
  (r/create-class
   {:display-name   "textarea/viewer"
    :reagent-render (fn [args]
                      [:div.preview__textarea
                       (display-string args)])}))
